import React, { useState, useEffect } from "react";
import { Dialog, DialogBody, DialogTitle } from "@/components/dialog";
import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import { Badge } from "@/components/badge";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { getPaperTypeLabel } from "@/constants/exam";

interface SubjectScore {
  subject: string;
  score?: number;
}

interface Student {
  id: string;
  userId: string;
  name: string;
  email: string;
  score: number;
  isRetake: boolean;
  subjectScores: SubjectScore[];
}

interface ScoreReportStudentFilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (selectedIds: string[]) => void;
  students: Student[];
  selectedStudentIds: string[];
}

const ScoreReportStudentFilterModal: React.FC<ScoreReportStudentFilterModalProps> = ({
  open,
  onClose,
  onApply,
  students,
  selectedStudentIds,
}) => {
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      setLocalSelectedIds(selectedStudentIds);
      setSearchQuery("");
    }
  }, [open, selectedStudentIds]);

  const filteredStudents = students.filter((student) => {
    const query = searchQuery.toLowerCase();
    return (
      student.name.toLowerCase().includes(query) ||
      student.email.toLowerCase().includes(query)
    );
  });

  const handleToggle = (studentId: string) => {
    setLocalSelectedIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    const allVisibleIds = filteredStudents.map((s) => s.id);
    const newSelection = new Set([...localSelectedIds, ...allVisibleIds]);
    setLocalSelectedIds(Array.from(newSelection));
  };

  const handleDeselectAll = () => {
    const visibleIds = new Set(filteredStudents.map((s) => s.id));
    setLocalSelectedIds(localSelectedIds.filter((id) => !visibleIds.has(id)));
  };

  const handleApply = () => {
    onApply(localSelectedIds);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelectedIds(selectedStudentIds);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} className="w-full max-w-2xl">
      <DialogTitle>Select Students</DialogTitle>
      <DialogBody>
        <div className="space-y-3">
          {/* Search and controls */}
          <div className="flex items-center gap-3">
            <InputGroup className="flex-1">
              <MagnifyingGlassIcon />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
            <Button plain onClick={handleSelectAll} className="text-xs">
              Select All
            </Button>
            <Button plain onClick={handleDeselectAll} className="text-xs">
              Deselect All
            </Button>
          </div>

          {/* Student list */}
          <div className="border border-gray-200 rounded overflow-hidden">
            {filteredStudents.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                No students found
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <Table dense>
                  <TableHead className="sticky top-0 bg-gray-50 z-10">
                    <TableRow>
                      <TableHeader className="w-16 !pl-4">
                        <Checkbox
                          checked={
                            filteredStudents.length > 0 &&
                            filteredStudents.every((s) =>
                              localSelectedIds.includes(s.id)
                            )
                          }
                          onChange={(checked) => {
                            if (checked) {
                              handleSelectAll();
                            } else {
                              handleDeselectAll();
                            }
                          }}
                        />
                      </TableHeader>
                      <TableHeader>Name</TableHeader>
                      <TableHeader>Score</TableHeader>
                      <TableHeader className="text-center">Total</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents.map((student) => (
                      <TableRow
                        key={student.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleToggle(student.id)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()} className="!pl-4">
                          <Checkbox
                            checked={localSelectedIds.includes(student.id)}
                            onChange={() => handleToggle(student.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {student.name}
                            </span>
                            {student.isRetake && (
                              <Badge color="amber" className="text-xs">
                                Retake
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {student.subjectScores.map((ss) => (
                              <div key={ss.subject} className="flex items-center gap-2">
                                <Badge color="zinc" className="text-xs">
                                  {getPaperTypeLabel(ss.subject)}
                                </Badge>
                                <span className="text-sm">{ss.score ?? '-'}</span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {student.score}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Summary and Actions */}
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-gray-500">
              {localSelectedIds.length} selected
            </span>
            <div className="flex gap-2">
              <Button plain onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
};

export default ScoreReportStudentFilterModal;